import React from 'react';
import { useTranslation } from 'utils/i18n';
import { formatPrice } from '../../ResourceDetailModal/utils/formatters';
import marketplaceTranslations from '../translations';

/**
 * Review Tab - Summary before creation
 * Compact, structured layout with grid design
 */
export const ReviewTab = ({
  formData,
  selectedProduct,
  selectedPrice,
  tenants,
  costPools,
  isDevResource,
  isCostPoolRequired
}) => {
  const { language } = useTranslation();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  const marketplace = selectedProduct?.marketplace || {};
  const provider = marketplace.provider;
  const category = marketplace.category || { displayName: 'Other' };
  
  const selectedTenant = tenants.find(t => t.id === formData.tenant_id);
  const selectedCostPool = costPools.find(cp => cp.id === formData.cost_pool_id);
  
  return (
    <div className="review-tab">
      <div className="review-header">
        <div className="review-header-content">
          <h3 className="review-title">{t('review.title', 'Review your configuration')}</h3>
          <p className="review-subtitle">{t('review.subtitle', 'Please review all details before proceeding. You can go back to make changes if needed.')}</p>
        </div>
      </div>
      
      <div className="review-grid">
        {/* Product Summary - Left Column */}
        {selectedProduct && (
          <div className="review-card review-card-primary">
            <div className="review-card-header">
              <div className="review-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </div>
              <h4 className="review-card-title">{t('review.productDetails', 'Product Details')}</h4>
            </div>
            <div className="review-card-body">
              <div className="review-row">
                <span className="review-label">{t('review.name', 'Product Name')}</span>
                <span className="review-value">{selectedProduct.name}</span>
              </div>
              {provider && (
                <div className="review-row">
                  <span className="review-label">{t('review.provider', 'Provider')}</span>
                  <span className="review-value">
                    {provider.displayName?.replace(/★/g, '').trim() || provider.displayName}
                  </span>
                </div>
              )}
              <div className="review-row">
                <span className="review-label">{t('review.category', 'Category')}</span>
                <span className="review-value">{category.displayName}</span>
              </div>
              {selectedPrice && (
                <div className="review-row review-row-highlight">
                  <span className="review-label">{t('review.pricing', 'Pricing Plan')}</span>
                  <span className="review-value review-value-pricing">
                    {selectedPrice.unit_amount === 0 ? (
                      <span className="price-free">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        {t('review.free', 'Free')}
                      </span>
                    ) : (
                      <span className="price-amount">
                        <strong>{formatPrice(selectedPrice.unit_amount, selectedPrice.currency)}</strong>
                        {selectedPrice.recurring && (
                          <span className="price-interval"> / {selectedPrice.recurring.interval}</span>
                        )}
                      </span>
                    )}
                    {selectedPrice.nickname && (
                      <span className="price-nickname">{selectedPrice.nickname}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Instance Details - Right Column */}
        <div className="review-card">
          <div className="review-card-header">
            <div className="review-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
            </div>
            <h4 className="review-card-title">{t('review.instanceConfiguration', 'Instance Configuration')}</h4>
          </div>
          <div className="review-card-body">
            <div className="review-row">
              <span className="review-label">{t('review.instanceName', 'Instance Name')}</span>
              <span className="review-value">{formData.name || <span className="review-value-empty">{t('review.notSet', 'Not set')}</span>}</span>
            </div>
            <div className="review-row">
              <span className="review-label">{t('review.resourceType', 'Resource Type')}</span>
              <span className="review-value">
                <span className="review-value-badge">{formData.type || 'bot'}</span>
              </span>
            </div>
            {isDevResource && (
              <div className="review-row">
                <span className="review-label">{t('review.environment', 'Environment')}</span>
                <span className="review-value">
                  <span className="badge badge-dev">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                    {t('review.development', 'Development')}
                  </span>
                </span>
              </div>
            )}
            {formData.description && (
              <div className="review-row review-row-full">
                <span className="review-label">{t('review.description', 'Description')}</span>
                <span className="review-value review-value-multiline">{formData.description}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Project Details - Bottom Row */}
        <div className="review-card review-card-full review-card-project">
          <div className="review-card-header">
            <div className="review-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h4 className="review-card-title">{t('review.projectBilling', 'Project & Billing')}</h4>
          </div>
          <div className="review-card-body review-card-body-horizontal">
            <div className="review-row">
              <span className="review-label">{t('review.tenant', 'Tenant')}</span>
              <span className="review-value">
                {selectedTenant ? (
                  <span className="review-value-text">{selectedTenant.name}</span>
                ) : (
                  <span className="review-value-empty">{formData.tenant_id || t('review.notSelected', 'Not selected')}</span>
                )}
              </span>
            </div>
            {(isCostPoolRequired && !isDevResource && formData.cost_pool_id) && (
              <div className="review-row">
                <span className="review-label">{t('review.costPool', 'Cost Pool')}</span>
                <span className="review-value">
                  {selectedCostPool ? (
                    <span className="review-value-text">{selectedCostPool.name}</span>
                  ) : (
                    <span className="review-value-text">{formData.cost_pool_id}</span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


